import type { Agent } from './agent';
import type {
  CompactBoundaryRecord,
  CompactionRecord,
  CompactSummaryRecord,
} from './compaction/types';
import type { AgentInputItemPool } from './runner/items';
import {
  getFunctionToolQualifiedName,
  resolveFunctionToolCallName,
} from './tool-identity';
import type { AgentInputItem } from './types';
import * as protocol from './types/protocol';
import { toSmartString } from './utils/smart-string';
export class RunItemBase<TRawItem = protocol.ModelItem> {
  public readonly type: string = 'base_item' as const;
  public rawItem?: TRawItem;

  toJSON() {
    return {
      type: this.type,
      rawItem: this.rawItem,
    };
  }
}

export class RunToolApprovalItem extends RunItemBase {
  public override readonly type = 'tool_approval_item' as const;
  constructor(
    public override rawItem:
      | protocol.FunctionCallItem
      | protocol.HostedToolCallItem,
    public agent: Agent<any, any>,
    /**
     * Explicit tool name to use for approval tracking when not present on the raw item.
     */
    public toolName?: string,
  ) {
    super();
    this.toolName = toolName ?? getDefaultApprovalToolName(rawItem, agent);
  }

  /**
   * Returns the tool name if available on the raw item or provided explicitly.
   * Kept for backwards compatibility with code that previously relied on `rawItem.name`.
   */
  get name(): string | undefined {
    return this.toolName ?? (this.rawItem as any).name;
  }
  /**
   * Returns the arguments if the raw item has an arguments property otherwise this will be undefined.
   */
  get arguments(): string | undefined {
    return 'arguments' in this.rawItem ? this.rawItem.arguments : undefined;
  }

  override toJSON() {
    return {
      ...super.toJSON(),
      agent: this.agent.toJSON(),
      toolName: this.toolName,
    };
  }
}

export class RunToolCallOutputItem extends RunItemBase<protocol.FunctionCallResultItem> {
  public override readonly type = 'tool_call_output_item' as const;

  constructor(
    public override rawItem: protocol.FunctionCallResultItem,
    public agent: Agent<any, any>,
    public output: string | unknown,
  ) {
    super();
  }

  override toJSON() {
    return {
      ...super.toJSON(),
      agent: this.agent.toJSON(),
      output: toSmartString(this.output),
    };
  }

  get callId(): string | undefined {
    return (
      getStringProperty(this.rawItem, 'callId') ??
      getStringProperty(this.rawItem, 'id')
    );
  }
}

export class RunReasoningItem extends RunItemBase<protocol.ReasoningItem> {
  public override readonly type = 'reasoning_item' as const;

  constructor(
    public override rawItem: protocol.ReasoningItem,
    public agent: Agent,
  ) {
    super();
  }

  override toJSON() {
    return {
      ...super.toJSON(),
      agent: this.agent.toJSON(),
    };
  }
}

export class RunHandoffCallItem extends RunItemBase<protocol.FunctionCallItem> {
  public override readonly type = 'handoff_call_item' as const;

  constructor(
    public override rawItem: protocol.FunctionCallItem,
    public agent: Agent,
  ) {
    super();
  }

  override toJSON() {
    return {
      ...super.toJSON(),
      agent: this.agent.toJSON(),
    };
  }
}

export class RunHandoffOutputItem extends RunItemBase<protocol.FunctionCallResultItem> {
  public override readonly type = 'handoff_output_item' as const;

  constructor(
    public override rawItem: protocol.FunctionCallResultItem,
    public sourceAgent: Agent<any, any>,
    public targetAgent: Agent<any, any>,
  ) {
    super();
  }

  override toJSON() {
    return {
      ...super.toJSON(),
      sourceAgent: this.sourceAgent.toJSON(),
      targetAgent: this.targetAgent.toJSON(),
    };
  }
}

export class RunToolCallItem extends RunItemBase<protocol.ToolCallItem> {
  public override readonly type = 'tool_call_item' as const;

  constructor(
    public override rawItem: protocol.ToolCallItem,
    public agent: Agent,
  ) {
    super();
  }

  override toJSON() {
    return {
      ...super.toJSON(),
      agent: this.agent.toJSON(),
    };
  }

  get toolName(): string | undefined {
    return getStringProperty(this.rawItem, 'name');
  }

  get callId(): string | undefined {
    return (
      getStringProperty(this.rawItem, 'callId') ??
      getStringProperty(this.rawItem, 'id')
    );
  }
}

export class RunMessageOutputItem extends RunItemBase<protocol.AssistantMessageItem> {
  public override readonly type = 'message_output_item' as const;

  constructor(
    public override rawItem: protocol.AssistantMessageItem,
    public agent: Agent,
  ) {
    super();
  }

  override toJSON() {
    return {
      ...super.toJSON(),
      agent: this.agent.toJSON(),
    };
  }

  get content(): string {
    let content = '';
    for (const part of this.rawItem.content) {
      if (part.type === 'output_text') {
        content += part.text;
      }
    }
    return content;
  }
}

export class RunCompactBoundaryItem extends RunItemBase<CompactBoundaryRecord> {
  public override readonly type = 'compact_boundary_item' as const;

  constructor(
    public override rawItem: CompactBoundaryRecord,
    public agent: Agent,
  ) {
    super();
  }

  override toJSON() {
    return {
      ...super.toJSON(),
      agent: this.agent.toJSON(),
    };
  }
}

export class RunCompactSummaryItem extends RunItemBase<CompactSummaryRecord> {
  public override readonly type = 'compact_summary_item' as const;

  constructor(
    public override rawItem: CompactSummaryRecord,
    public agent: Agent,
  ) {
    super();
  }

  override toJSON() {
    return {
      ...super.toJSON(),
      agent: this.agent.toJSON(),
    };
  }
}

export type RunItem =
  | RunMessageOutputItem
  | RunToolCallItem
  | RunReasoningItem
  | RunHandoffCallItem
  | RunToolCallOutputItem
  | RunHandoffOutputItem
  | RunToolApprovalItem
  | RunCompactBoundaryItem
  | RunCompactSummaryItem;

export type RunCompactItem = RunCompactBoundaryItem | RunCompactSummaryItem;

export function isRunCompactItem(item: RunItem): item is RunCompactItem {
  return (
    item.type === 'compact_boundary_item' ||
    item.type === 'compact_summary_item'
  );
}

export function getCompactionRecordFromRunItem(
  item: RunItem,
): CompactionRecord | undefined {
  return isRunCompactItem(item) ? item.rawItem : undefined;
}

function getStringProperty(item: object, key: string): string | undefined {
  const value = (item as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : undefined;
}

function getDefaultApprovalToolName(
  rawItem: RunToolApprovalItem['rawItem'],
  agent: Agent<any, any>,
): string | undefined {
  if (rawItem.type !== 'function_call') {
    return (rawItem as any).name;
  }

  const availableFunctionTools = new Map(
    agent.tools.flatMap((tool) => {
      if (tool.type !== 'function' || typeof tool.name !== 'string') {
        return [];
      }
      return [[getFunctionToolQualifiedName(tool) ?? tool.name, tool] as const];
    }),
  );

  const resolvedToolName = resolveFunctionToolCallName(
    rawItem,
    availableFunctionTools,
  );

  if (
    typeof rawItem.name === 'string' &&
    typeof rawItem.namespace === 'string' &&
    rawItem.namespace === rawItem.name &&
    !availableFunctionTools.has(`${rawItem.namespace}.${rawItem.name}`)
  ) {
    return rawItem.name;
  }

  return resolvedToolName ?? rawItem.name;
}

export function takeAgentInputFromPool(
  pool: AgentInputItemPool,
  key: string,
): AgentInputItem | undefined {
  const candidates = pool.get(key);
  if (!candidates || candidates.length === 0) {
    return undefined;
  }
  const [first] = candidates;
  candidates.shift();
  if (candidates.length === 0) {
    pool.delete(key);
  }
  return first;
}

/**
 * Extract all text output from a list of run items by concatenating the content of all
 * message output items.
 *
 * @param items - The list of run items to extract text from.
 * @returns A string of all the text output from the run items.
 */
export function extractAllTextOutput(items: RunItem[]) {
  return items
    .filter((item) => item.type === 'message_output_item')
    .map((item) => item.content)
    .join('');
}
